import { useMemo } from 'react';
import { hasExactPermission, SYSTEM_ADMIN_PERMISSION, ACCOUNT_ADMIN_PERMISSION } from './permissionChecks';

interface User {
  username?: string;
  email?: string;
  [key: string]: any;
}

export function usePermissions(user: User | null | undefined, permissions: string[] = [], resource?: string) {
  const isSystemAdmin = useMemo(() => {
    return hasExactPermission(permissions, SYSTEM_ADMIN_PERMISSION);
  }, [permissions]);

  const isAccountAdmin = useMemo(() => {
    if (isSystemAdmin) return false;
    return hasExactPermission(permissions, ACCOUNT_ADMIN_PERMISSION);
  }, [permissions]);

  // Account-level resources that require system:admin for create/delete.
  // Account admins can read/update these but only system admins can create or delete them.
  const isSystemLevelResource = resource === 'accounts';

  const hasPermission = useMemo(() => {
    return (permission: string): boolean => {
      if (isSystemAdmin || isAccountAdmin) return true;
      return permissions.includes(permission);
    };
  }, [isSystemAdmin, isAccountAdmin, permissions]);

  const canCreate = useMemo(() => {
    if (isSystemAdmin) return true;
    if (isAccountAdmin && !isSystemLevelResource) return true;
    if (!resource) return false;
    return permissions.includes(`${resource}:create`) || permissions.includes(`${resource}:write`);
  }, [isSystemAdmin, isAccountAdmin, isSystemLevelResource, permissions, resource]);

  const canUpdate = useMemo(() => {
    if (isSystemAdmin || isAccountAdmin) return true;
    if (!resource) return false;
    return permissions.includes(`${resource}:update`) || permissions.includes(`${resource}:write`);
  }, [isSystemAdmin, isAccountAdmin, permissions, resource]);

  const canDelete = useMemo(() => {
    if (isSystemAdmin) return true;
    if (isAccountAdmin && !isSystemLevelResource) return true;
    if (!resource) return false;
    return permissions.includes(`${resource}:delete`) || permissions.includes(`${resource}:write`);
  }, [isSystemAdmin, isAccountAdmin, isSystemLevelResource, permissions, resource]);

  const canRead = useMemo(() => {
    if (isSystemAdmin || isAccountAdmin) return true;
    if (!resource) return false;
    return permissions.includes(`${resource}:read`) || permissions.includes(`${resource}:write`) || canCreate || canUpdate || canDelete;
  }, [isSystemAdmin, isAccountAdmin, permissions, resource, canCreate, canUpdate, canDelete]);

  return {
    isSystemAdmin,
    isAccountAdmin,
    hasPermission,
    canCreate,
    canUpdate,
    canDelete,
    canRead,
  };
}

export default usePermissions;
